<template>
  <TemplateOnboarding
    type="settings"
    :loading="loading"
    :footer="false"
  >
    <div class="settings-page">
      <section class="banner-section">
        <CalicoBanner
          v-if="isCancelingState && cancellationMessage"
          variant="info"
          :fixed="true"
        >
          {{ cancellationMessage }}
        </CalicoBanner>
      </section>
      <section class="password-section">
        <div class="password-header">
          <h4 class="label--large">
            {{ SettingsEnum.COPY.PASSWORD_TITLE }}
          </h4>
        </div>

        <p class="body--small">
          {{ SettingsEnum.COPY.PASSWORD_DESCRIPTION }}
        </p>

        <div class="password-action">
          <button
            class="button-utility button-utility--small"
            @click="showPasswordForm = !showPasswordForm"
          >
            {{ SettingsEnum.COPY.CHANGE_PASSWORD }}
          </button>
        </div>

        <div
          v-if="showPasswordForm"
          class="password-form"
        >
          <CalicoForm
            v-slot="{ field, error }"
            name="CurrentPassword"
            :label="SettingsEnum.COPY.CURRENT_PASSWORD_LABEL"
            rules="required"
          >
            <CalicoField
              v-model="currentPassword"
              v-bind="field"
              :type="showCurrentPassword ? 'text' : 'password'"
              :error="error"
              icon="eye"
              @click="toggleCurrentPassword"
            />
          </CalicoForm>

          <CalicoForm
            v-slot="{ field, error }"
            name="NewPassword"
            :label="SettingsEnum.COPY.NEW_PASSWORD_LABEL"
            rules="required|password"
          >
            <CalicoField
              v-model="newPassword"
              v-bind="field"
              :type="showNewPassword ? 'text' : 'password'"
              :error="error"
              icon="eye"
              @click="toggleNewPassword"
            />
          </CalicoForm>

          <CalicoForm
            v-slot="{ field, error }"
            name="ConfirmPassword"
            :label="SettingsEnum.COPY.CONFIRM_PASSWORD_LABEL"
            rules="required|confirmed:NewPassword"
          >
            <CalicoField
              v-model="confirmPassword"
              v-bind="field"
              :type="showConfirmPassword ? 'text' : 'password'"
              :error="error"
              icon="eye"
              @click="toggleConfirmPassword"
            />
          </CalicoForm>

          <p
            v-if="passwordSuccess"
            class="password-success body--small"
          >
            {{ SettingsEnum.COPY.PASSWORD_UPDATED }}
          </p>

          <p
            v-if="passwordError"
            class="password-error body--small"
          >
            {{ passwordError }}
          </p>

          <div class="password-form-actions">
            <button
              class="button-primary button-primary--small"
              :disabled="savingPassword"
              @click="handleChangePassword"
            >
              {{ savingPassword ? SettingsEnum.COPY.SAVING_PASSWORD : SettingsEnum.COPY.SAVE_PASSWORD }}
            </button>

            <button
              class="button-secondary button-secondary--small"
              :disabled="savingPassword"
              @click="closePasswordForm"
            >
              {{ SettingsEnum.COPY.CANCEL }}
            </button>
          </div>
        </div>
      </section>
      <section class="subscription-section">
        <div class="subscription-header">
          <h4 class="label--large">
            {{ SettingsEnum.COPY.SUBSCRIPTION_TITLE }}
          </h4>
        </div>

        <div class="subscription-card">
          <CalicoSubscriptionCard
            v-if="isOwner && subscriptionCardConfig"
            :tier="subscriptionCardConfig.tier"
            :info-cards="subscriptionCardConfig.infoCards"
            :features="subscriptionCardConfig.features"
            :billing-label="subscriptionCardConfig.billingLabel"
            :billing-prefix="subscriptionCardConfig.billingPrefix"
            :next-billing-date="subscriptionCardConfig.nextBillingDate"
            :buttons="subscriptionCardConfig.buttons"
            :cancellation-message="cancellationMessage"
            @manage="goToManage"
            @reactivate="goToManage"
            @upgrade="goToManage"
          />

          <div
            v-else-if="!isOwner"
            class="no-access body--small"
          >
            {{ SettingsEnum.COPY.NO_ACCESS }}
          </div>
        </div>

        <div
          v-if="isOwner && subscription && isActiveState"
          class="billing-history"
        >
          <div class="billing-history-csv">
            <button
              v-if="payments.length"
              class="button-secondary button-secondary--small"
              :disabled="downloadingCsv"
              @click="handleDownloadCsv"
            >
              <i class="bi bi-download" />
              {{ downloadingCsv ? SettingsEnum.COPY.DOWNLOADING_CSV : SettingsEnum.COPY.DOWNLOAD_CSV }}
            </button>
          </div>

          <div
            v-if="paymentsLoading"
            class="billing-history-loading body--small"
          >
            {{ SettingsEnum.COPY.LOADING_PAYMENTS }}
          </div>

          <div
            v-else-if="payments.length"
            class="billing-container"
          >
            <CalicoBillingItem
              v-for="payment in paginatedPayments"
              :key="payment.id"
              :amount="payment.amount"
              :currency="payment.currency"
              :date="payment.createdAt"
            />
          </div>

          <p
            v-if="!paymentsLoading && !payments.length"
            class="body--small"
          >
            {{ SettingsEnum.COPY.NO_TRANSACTIONS }}
          </p>

          <CalicoPagination
            v-if="totalPages > 1"
            :pages="totalPages"
          />
        </div>
      </section>
    </div>
  </TemplateOnboarding>
</template>

<script>
import TemplateOnboarding from "@/components/template/onboarding/index.vue";
import CalicoSubscriptionCard from "@/components/calico/subscriptionCard/index.vue";
import CalicoBillingItem from "@/components/calico/billingItem/index.vue";
import CalicoPagination from "@/components/calico/pagination/index.vue";
import CalicoForm from "@/components/pattern/form/index.vue";
import CalicoField from "@/components/calico/field/index.vue";
import services from "@/services";
import { RoleEnum, RoutesEnum, SubscriptionCopy, StripeCopy, SettingsEnum } from "@/enums";
import { formatCurrency, formatDisplayDate } from "@/utils/helper";
import { downloadCsv } from "@/utils/csv";
import { getAuth, reauthenticateWithCredential, updatePassword, EmailAuthProvider } from "firebase/auth";
import CalicoBanner from "@/components/calico/banner/index.vue";

const ACTIVE_STATE = SubscriptionCopy.STATE.ACTIVE;
const INACTIVE_STATE = SubscriptionCopy.STATE.INACTIVE;
const TRIAL_STATE = SubscriptionCopy.STATE.TRIAL;
const PAGE_LIMIT = SettingsEnum.PAGINATION.PAGE_LIMIT;

export default {
  name: "SettingsPage",

  components: {
    TemplateOnboarding,
    CalicoSubscriptionCard,
    CalicoBillingItem,
    CalicoPagination,
    CalicoForm,
    CalicoField,
    CalicoBanner,
  },

  data() {
    return {
      SettingsEnum,
      RoutesEnum,
      loading: true,
      paymentsLoading: false,
      downloadingCsv: false,
      subscription: null,
      payments: [],
      isOwner: false,
      showPasswordForm: false,
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
      showCurrentPassword: false,
      showNewPassword: false,
      showConfirmPassword: false,
      savingPassword: false,
      passwordSuccess: false,
      passwordError: "",
    }
  },

  computed: {
    subscriptionState() {
      return this.subscription?.state || null;
    },

    isActiveState() {
      return this.subscriptionState === ACTIVE_STATE;
    },

    currentPage() {
      return Math.max(Number(this.$route.query.page) || 1, 1)
    },

    totalPages() {
      return Math.ceil(this.payments.length / PAGE_LIMIT)
    },

    paginatedPayments() {
      const start = (this.currentPage - 1) * PAGE_LIMIT
      return this.payments.slice(start, start + PAGE_LIMIT)
    },

    subscriptionCardConfig() {
      if (!this.subscription) return null;

      const planCopy =
        SubscriptionCopy.PLANS[this.subscription.plan] ||
        SubscriptionCopy.PLANS.BUSINESS_OWNER;

      const infoCards = (this.subscription.pricingCards || []).map((c) => ({
        label: SubscriptionCopy.LABEL[c.labelKey] || c.labelKey,
        content: c.value,
      }));

      const billingPrefix =
        this.subscription.billing?.labelKey === "TRIAL_ENDS"
          ? SubscriptionCopy.LABEL.TRIAL_ENDS
          : formatCurrency(this.subscription.pricingCards?.[0]?.value) || "";

      const nextBillingDate =
        this.subscription.billing?.dateISO || "";

      const buttonConfigMap = {
        upgrade: SubscriptionCopy.BUTTON.UPGRADE,
        manage: SubscriptionCopy.BUTTON.MANAGE_SUBSCRIPTION,
        reactivate: SubscriptionCopy.BUTTON.REACTIVATE,
      };

      const buttons = (this.subscription.buttons || []).map((btn) => {
        const config = buttonConfigMap[btn];
        if (!config) return null;
        return {
          label: config.label,
          variant: "primary",
          event: config.event,
        };
      }).filter(Boolean);

      return {
        tier: planCopy.title,
        infoCards,
        features: planCopy.features || [],
        billingLabel: SubscriptionCopy.LABEL.NEXT_BILLING,
        billingPrefix,
        nextBillingDate,
        buttons,
      };
    },

    isCancelingState() {
      return this.subscriptionState === SubscriptionCopy.STATE.CANCELING
    },

    cancellationMessage() {
      if (!this.isCancelingState || !this.subscription?.cancelAt) return null
      return `${SettingsEnum.COPY.CANCELLATION_MESSAGE} ${formatDisplayDate(this.subscription.cancelAt)}`
    },
  },

  async mounted() {
    this.loading = true;

    try {
      const user = await services.user.get();
      const role = String(user?.role?.name || "").trim();

      this.isOwner =
        role === RoleEnum.OWNER ||
        role === RoleEnum.BUSINESS_ADMIN;

      if (!this.isOwner) return;

      this.subscription = await services.billing.getSubscriptionSummary();

      if (this.subscription?.state === INACTIVE_STATE) {
        this.$router.push(RoutesEnum.SETTINGS_MANAGE_SUBSCRIPTION);
      }

      if (this.isActiveState) {
        await this.loadPayments()
      }
    } catch (error) {
      console.error("[SettingsPage] Failed to load settings data", error);
    } finally {
      this.loading = false;
    }
  },

  methods: {
    toggleCurrentPassword(e) {
      if (e.target.tagName.toLowerCase() === 'i') {
        this.showCurrentPassword = !this.showCurrentPassword
      }
    },

    toggleNewPassword(e) {
      if (e.target.tagName.toLowerCase() === 'i') {
        this.showNewPassword = !this.showNewPassword
      }
    },

    toggleConfirmPassword(e) {
      if (e.target.tagName.toLowerCase() === 'i') {
        this.showConfirmPassword = !this.showConfirmPassword
      }
    },

    closePasswordForm() {
      this.showPasswordForm = false
      this.currentPassword = ""
      this.newPassword = ""
      this.confirmPassword = ""
      this.showCurrentPassword = false
      this.showNewPassword = false
      this.showConfirmPassword = false
      this.passwordSuccess = false
      this.passwordError = ""
    },

    async handleChangePassword() {
      this.savingPassword = true
      this.passwordSuccess = false
      this.passwordError = ""

      try {
        const auth = getAuth()
        const user = auth.currentUser

        if (!user) throw new Error(SettingsEnum.COPY.PASSWORD_ERROR_NO_USER)

        const credential = EmailAuthProvider.credential(user.email, this.currentPassword)
        await reauthenticateWithCredential(user, credential)
        await updatePassword(user, this.newPassword)

        this.passwordSuccess = true
        this.currentPassword = ""
        this.newPassword = ""
        this.confirmPassword = ""
      } catch (error) {
        if (error.code === "auth/wrong-password" || error.code === "auth/invalid-credential") {
          this.passwordError = SettingsEnum.COPY.PASSWORD_ERROR_WRONG_CURRENT
        } else {
          this.passwordError = error.message
        }
      } finally {
        this.savingPassword = false
      }
    },

    async loadPayments() {
      this.paymentsLoading = true;
      try {
        this.payments = await services.billing.getUserPayments()
      } catch (error) {
        console.error("[SettingsPage] Failed to load payments", error)
        this.payments = []
      } finally {
        this.paymentsLoading = false;
      }
    },

    handleDownloadCsv() {
      this.downloadingCsv = true
      try {
        downloadCsv({
          headers: SettingsEnum.CSV.HEADERS,
          rows: this.payments.map((p) => [
            formatCurrency(p.amount, p.currency || 'USD', 'en-US', true),
            p.currency?.toUpperCase() || 'USD',
            formatDisplayDate(p.createdAt),
          ]),
          filename: SettingsEnum.CSV.FILENAME,
        })
      } catch (error) {
        console.error("[SettingsPage] Failed to download CSV", error)
      } finally {
        this.downloadingCsv = false;
      }
    },

    async goToManage() {
      const state = this.subscription?.state;
      const CANCELING_STATE = SubscriptionCopy.STATE.CANCELING;

      if (state === ACTIVE_STATE || state === TRIAL_STATE || state === CANCELING_STATE) {
        this.loading = true;
        try {
          const { url } = await services.billing.createPortalSession();
          if (!url) throw new Error(StripeCopy.MANAGE_SUBSCRIPTION.GENERIC_ERROR);
          window.location.href = url;
        } catch (err) {
          console.error("[SettingsPage] goToManage failed", err);
          this.loading = false;
        }
        return;
      }

      this.$router.push(RoutesEnum.SETTINGS_MANAGE_SUBSCRIPTION);
    },
  },
}
</script>

<style scoped lang="scss">
.settings-page {
  .password-section {
    @include stack-space($space-large);
    @include inset-space($space-large);
    align-items: flex-start;
    align-self: stretch;

    .password-header {
      @include inline-space($space-none);
      align-items: baseline;
      align-self: stretch;
      border-bottom: 2px solid $color-border-primary;
    }

    .password-action {
      align-self: flex-start;
    }

    .password-form {
      @include stack-space($space-medium);
      align-self: stretch;

      .password-form-actions {
        @include inline-space($space-small);
        align-self: flex-start;
      }
      .button-primary {
        border: solid $color-transparent $border-width-default;
      }
    }

    .password-success {
      color: $color-semantics-success;
    }

    .password-error {
      color: $color-semantics-error;
    }
  }

  .banner-section{
    @include stack-space($space-large);
    @include inset-space($space-large);
  }
  .subscription-section {
    @include stack-space($space-large);
    @include inset-space($space-large);
    align-items: flex-start;
    align-self: stretch;

    .subscription-header {
      @include inline-space($space-none);
      align-items: baseline;
      align-self: stretch;
      border-bottom: 2px solid $color-border-primary;
    }

    .subscription-card {
      align-self: stretch;
    }

    .billing-history {
      @include stack-space($space-small);
      align-self: stretch;
      padding-bottom: $space-xlarge;

      .billing-history-csv {
        align-self: flex-end;
      }

      .billing-history-loading {
        color: $color-neutral;
      }

      .billing-container {
        margin: $space-large 0;
      }

      .calico-pagination {
        padding: 0 0 $space-large 0;
      }
    }
  }
}
</style>